import React, { useState, useEffect, useRef } from 'react';
import { X, Phone, Clock, User, FileText, Calendar, RefreshCw, ChevronUp } from 'lucide-react';
import { DetailedCallLog, DetailedCallLogService, CallLogFilters } from './DetailedCallLogService';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';

interface CallDetailsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    callType: 'ATTEMPTED' | 'CONNECTED' | 'RECEIVED' | 'MISSED';
    filters: Omit<CallLogFilters, 'callType'>;
}
const CallDetailsPanel: React.FC<CallDetailsPanelProps> = ({
    isOpen,
    onClose,
    callType,
    filters
}) => {
    const navigate = useNavigate();
    // const [callLogs, setCallLogs] = useState<DetailedCallLog[]>([]);
    const [callLogs, setCallLogs] = useState<DetailedCallLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [panelWidth, setPanelWidth] = useState(600); // Default width in pixels (increased from 384px)
    const [isResizing, setIsResizing] = useState(false);
    // Add state to track if date range was limited
    const [isDateRangeLimited, setIsDateRangeLimited] = useState(false);
    const [actualStartTime, setActualStartTime] = useState<string | null>(null);
    const [actualEndTime, setActualEndTime] = useState<string | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Fetch call logs when panel opens or filters change
    useEffect(() => {
        if (isOpen) {
            fetchCallLogs();
        }
    }, [isOpen, callType, filters]);

    // Handle keyboard events
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevent body scroll when panel is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'auto';
        };
    }, [isOpen, onClose]);

    // Add custom scrollbar styles
    useEffect(() => {
        const styleId = 'call-details-panel-scrollbar-styles';

        if (isOpen && !document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `;
            document.head.appendChild(style);
        }

        return () => {
            const style = document.getElementById(styleId);
            if (style && !isOpen) {
                document.head.removeChild(style);
            }
        };
    }, [isOpen]);

    // Handle scroll events to show shadow when scrolled
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        setIsScrolled(scrollTop > 10);
    };

    // Scroll to top function
    const scrollToTop = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Close panel with expansion reset
    const handleClosePanel = () => {
        setPanelWidth(600); // Reset to default width when closing
        onClose();
    };

    // Handle mouse down on resize handle
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    };

    // Handle mouse move for resizing
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;

            const newWidth = window.innerWidth - e.clientX;
            const minWidth = 300; // Minimum panel width
            const maxWidth = window.innerWidth * 0.8; // Maximum 80% of screen width

            setPanelWidth(Math.max(minWidth, Math.min(maxWidth, newWidth)));
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
            document.body.style.userSelect = 'auto';
        };
    }, [isResizing]);

    const fetchCallLogs = async () => {
        setLoading(true);
        setError(null);

        try {
            let startTime = filters.startTime;
            let endTime = filters.endTime;
            let isDateRangeLimited = false;

            // Check if date range is provided and calculate the difference
            if (startTime && endTime) {
                const start = new Date(startTime);
                const end = new Date(endTime);
                const daysDifference = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

                // Only override to 31 days if the range is greater than 31 days
                if (daysDifference > 31) {
                    const thirtyOneDaysAgo = new Date();
                    thirtyOneDaysAgo.setDate(thirtyOneDaysAgo.getDate() - 31);
                    thirtyOneDaysAgo.setHours(0, 0, 0, 0);

                    const today = new Date();
                    today.setHours(23, 59, 59, 999);

                    startTime = thirtyOneDaysAgo.toISOString();
                    endTime = today.toISOString();
                    isDateRangeLimited = true;
                }
            } else {
                // If no date range is provided, default to last 31 days
                const thirtyOneDaysAgo = new Date();
                thirtyOneDaysAgo.setDate(thirtyOneDaysAgo.getDate() - 31);
                thirtyOneDaysAgo.setHours(0, 0, 0, 0);

                const today = new Date();
                today.setHours(23, 59, 59, 999);

                startTime = thirtyOneDaysAgo.toISOString();
                endTime = today.toISOString();
                isDateRangeLimited = true;
            }

            // Update state with the actual date range being used
            setIsDateRangeLimited(isDateRangeLimited);
            setActualStartTime(startTime);
            setActualEndTime(endTime);

            const callLogFilters: CallLogFilters = {
                ...filters,
                callType,
                startTime,
                endTime
            };

            console.log('Fetching call logs with filters:', {
                ...callLogFilters,
                userCount: callLogFilters.userIds?.length || 0,
                isAllStaff: !callLogFilters.userIds || callLogFilters.userIds.length === 0 || callLogFilters.userIds.length > 30,
                isDateRangeLimited,
                dateRange: `${new Date(startTime).toLocaleDateString()} - ${new Date(endTime).toLocaleDateString()}`
            });
            const logs = await DetailedCallLogService.fetchDetailedCallLogs(db, callLogFilters);
            console.log(`Received ${logs.length} logs for ${callType}:`, {
                totalLogs: logs.length,
                uniqueUsers: new Set(logs.map(log => log.userId)).size,
                callType,
                logs: logs.slice(0, 5) // Show first 5 logs as sample
            });
            setCallLogs(logs);
        } catch (err) {
            console.error('Error fetching call logs:', err);
            setError('Failed to fetch call logs');
        } finally {
            setLoading(false);
        }
    };

    const handleStudentClick = (studentId: string) => {
        navigate(`/studentdetails/${studentId}`);
    };

    const getCallTypeInfo = () => {
        switch (callType) {
            case 'ATTEMPTED':
                return {
                    title: 'Attempted Calls',
                    description: 'All outbound call attempts',
                    color: 'text-indigo-600',
                    bgColor: 'bg-indigo-50',
                    borderColor: 'border-indigo-200'
                };
            case 'CONNECTED':
                return {
                    title: 'Connected Calls',
                    description: 'All outbound Connected calls',
                    color: 'text-cyan-600',
                    bgColor: 'bg-cyan-50',
                    borderColor: 'border-cyan-200'
                };
            case 'RECEIVED':
                return {
                    title: 'Received Calls',
                    description: 'All incoming calls',
                    color: 'text-sky-600',
                    bgColor: 'bg-sky-50',
                    borderColor: 'border-sky-200'
                };
            case 'MISSED':
                return {
                    title: 'Missed Calls',
                    description: 'All missed calls',
                    color: 'text-red-600',
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200'
                };
            default:
                return {
                    title: 'Call Details',
                    description: 'Call information',
                    color: 'text-gray-600',
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200'
                };
        }
    };

    const typeInfo = getCallTypeInfo();

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black z-40 transition-all duration-300 ${isOpen ? 'bg-opacity-25 visible' : 'bg-opacity-0 invisible'
                    }`}
                onClick={handleClosePanel}
            />

            {/* Panel */}
            <div
                className={`fixed right-0 top-0 h-full max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
                    }`}
                style={{ width: `${panelWidth}px` }}
            >
                {/* Resize Handle */}
                <div
                    className="absolute left-0 top-0 w-1 h-full bg-gray-300 hover:bg-blue-500 cursor-ew-resize z-10 transition-colors duration-200"
                    onMouseDown={handleMouseDown}
                    title="Drag to resize"
                />
                {/* Header */}
                <div className={`${typeInfo.bgColor} ${typeInfo.borderColor} border-b p-4 flex-shrink-0`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className={`text-lg font-semibold ${typeInfo.color}`}>
                                {typeInfo.title}
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {typeInfo.description}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={fetchCallLogs}
                                disabled={loading}
                                className="p-2 hover:bg-white rounded-full transition-colors duration-200 disabled:opacity-50"
                                title="Refresh data"
                            >
                                <RefreshCw size={16} className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                                onClick={handleClosePanel}
                                className="p-2 hover:bg-white rounded-full transition-colors duration-200"
                            >
                                <X size={20} className="text-gray-600" />
                            </button>
                        </div>
                    </div>

                    {/* Stats Summary */}
                    <div className="mt-3 flex items-center gap-4 text-sm flex-wrap">
                        <span className={`${typeInfo.color} font-medium`}>
                            Total: {callLogs.length}
                        </span>

                        {/* {callLogs.length > 0 && (
                            <span className="text-gray-600">
                                Staff: {new Set(callLogs.map(log => log.userId)).size} members
                            </span>
                        )} */}

                        {/* {callType === 'CONNECTED' && callLogs.length > 0 && (
                            <span className="text-gray-600">
                                Avg Duration: {
                                    DetailedCallLogService.formatDuration(
                                        callLogs.reduce((sum, log) => sum + log.duration, 0) / callLogs.length
                                    )
                                }
                            </span>
                        )} */}
                        {(callType === 'ATTEMPTED' || callType === 'CONNECTED') && callLogs.length > 0 && (
                            <span className="text-gray-600">
                                Total Duration: {
                                    DetailedCallLogService.formatDuration(
                                        callLogs.reduce((sum, log) => sum + log.duration, 0)
                                    )
                                }
                            </span>
                        )}
                    </div>

                    {/* Filter Info */}
                    <div className="mt-2 text-xs text-gray-500">
                        {isDateRangeLimited ? (
                            <div>
                                <span className="text-blue-600 font-medium">Period: Last 31 days</span>
                                <span className="text-orange-600 ml-2">(limited from longer range)</span>
                            </div>
                        ) : actualStartTime && actualEndTime ? (
                            <div>
                                Period: {new Date(actualStartTime).toLocaleDateString()} - {(() => {
                                    // Display the original end date by subtracting the extra day that was added for querying
                                    const displayEndDate = new Date(actualEndTime);
                                    // Check if this looks like a modified end date (has time set to almost end of day)
                                    if (displayEndDate.getHours() === 5 && displayEndDate.getMinutes() === 29) {
                                        displayEndDate.setDate(displayEndDate.getDate() - 1);
                                    }
                                    return displayEndDate.toLocaleDateString();
                                })()}
                            </div>
                        ) : null}
                        {filters.userIds && filters.userIds.length > 0 ? (
                            <div>
                                Staff: {filters.userIds.length} selected
                                {filters.userIds.length > 30 && (
                                    <span className="ml-1 text-blue-600">(showing all staff)</span>
                                )}
                            </div>
                        ) : (
                            <div>
                                Staff: <span className="text-blue-600">All staff members</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Scroll indicator shadow */}
                <div
                    className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-gray-200 to-transparent pointer-events-none transition-opacity duration-200 z-10 ${isScrolled ? 'opacity-50' : 'opacity-0'
                        }`}
                    style={{ top: '120px' }} // Adjust based on header height
                />

                {/* Content */}
                <div
                    ref={contentRef}
                    className={`flex-1 overflow-y-auto custom-scrollbar min-h-0 transition-shadow duration-200 ${isScrolled ? 'shadow-inner' : ''
                        }`}
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e1 #f1f5f9'
                    }}
                    onScroll={handleScroll}
                >
                    {loading ? (
                        <div className="p-4">
                            <div className="text-center mb-4">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <p className="text-sm text-gray-600 mt-2">Loading call details...</p>
                            </div>
                            <div className="animate-pulse space-y-3">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="border border-gray-200 rounded-lg p-3">
                                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-4 text-center">
                            <div className="text-red-600 mb-2">
                                <FileText size={24} className="mx-auto mb-2" />
                                {error}
                            </div>
                            <button
                                onClick={fetchCallLogs}
                                className="text-sm text-blue-600 hover:text-blue-800 mr-4"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => {
                                    // Generate sample data for testing
                                    const sampleData: DetailedCallLog[] = [
                                        {
                                            id: 'sample-1',
                                            userId: 'user-1',
                                            userName: 'John Doe',
                                            leadUid: 'lead-1',
                                            leadName: 'Sample Lead 1',
                                            phoneNumber: '+1234567890',
                                            type: callType === 'RECEIVED' ? 'INCOMING' : callType === 'MISSED' ? 'MISSED' : 'OUTGOING',
                                            duration: callType === 'CONNECTED' ? 120 : callType === 'MISSED' ? 0 : Math.random() > 0.5 ? 90 : 0,
                                            callTimestamp: { toDate: () => new Date() } as any,
                                            notes: `Sample ${callType.toLowerCase()} call`
                                        }
                                    ];
                                    setCallLogs(sampleData);
                                }}
                                className="text-sm text-green-600 hover:text-green-800"
                            >
                                Load Sample Data
                            </button>
                        </div>
                    ) : callLogs.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                            <Phone size={24} className="mx-auto mb-2 opacity-50" />
                            <p>No {callType.toLowerCase()} calls found</p>
                            <p className="text-sm mt-1">
                                for {filters.userIds && filters.userIds.length > 0
                                    ? `${filters.userIds.length} selected staff members`
                                    : 'all staff members'
                                }
                            </p>
                            <p className="text-sm text-gray-400">in the selected time period</p>
                            {callType === 'CONNECTED' && (
                                <p className="text-xs mt-2 text-gray-400">
                                    Connected calls are outbound calls with duration &gt; 0
                                </p>
                            )}
                            {callType === 'ATTEMPTED' && (
                                <p className="text-xs mt-2 text-gray-400">
                                    Attempted calls include all outbound call attempts
                                </p>
                            )}
                            {callType === 'RECEIVED' && (
                                <p className="text-xs mt-2 text-gray-400">
                                    Received calls are incoming calls that were answered
                                </p>
                            )}
                            {callType === 'MISSED' && (
                                <p className="text-xs mt-2 text-gray-400">
                                    Missed calls were not answered
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className={`p-4 pb-6 ${panelWidth > 600 ? "grid grid-cols-2 gap-4" : "space-y-3"}`}>
                            {callLogs.map((log) => {
                                const typeDisplay = DetailedCallLogService.getCallTypeDisplay(log.type);

                                return (
                                    <div
                                        key={log.id}
                                        className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow duration-200"
                                    >
                                        {/* Call Header */}
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-gray-900 truncate">
                                                        {log.leadName}
                                                    </span>
                                                    <span className={`text-xs px-2 py-1 rounded-full ${typeDisplay.color}`}>
                                                        {typeDisplay.label}
                                                    </span>
                                                    {callType === 'CONNECTED' && log.duration > 0 && (
                                                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                                            Connected
                                                        </span>
                                                    )}
                                                    {callType === 'ATTEMPTED' && log.duration === 0 && (
                                                        <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                                                            No Answer
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-600 flex items-center gap-2">
                                                    {/* <span>{log.phoneNumber}</span> */}
                                                    {/* {log.duration > 0 && (
                    <span className="text-green-600 font-medium">
                        • {DetailedCallLogService.formatDuration(log.duration)}
                    </span>
                )} */}
                                                </div>
                                            </div>
                                            {log.duration > 0 && (
                                                <div className="flex items-center gap-1 text-sm text-gray-600">
                                                    <Clock size={14} />
                                                    {DetailedCallLogService.formatDuration(log.duration)}
                                                </div>
                                            )}
                                        </div>

                                        {/* Call Details */}
                                        <div className="space-y-1 text-xs text-gray-500">
                                            <div className="flex items-center gap-1">
                                                <User size={12} />
                                                {log.userName}
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-1">
                                                    <Calendar size={12} />
                                                    {DetailedCallLogService.formatTimestamp(log.callTimestamp)}
                                                </div>

                                                {log.outcomeName && (
                                                    <div className="flex items-center gap-1">
                                                        <FileText size={12} />
                                                        <span className="text-blue-600">{log.outcomeName}</span>
                                                    </div>
                                                )}
                                            </div>


                                            {log.notes && (
                                                <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                                    <div className="font-medium text-gray-700 mb-1">Notes:</div>
                                                    <div className="text-gray-600">{log.notes}</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Recording Indicator */}
                                        {log.recording && (
                                            <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                                                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                                Recording available
                                            </div>
                                        )}

                                        {/* View Details Button */}
                                        <div className="mt-4 flex justify-end">
                                            <button
                                                onClick={() => handleStudentClick(log.leadUid)}
                                                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 border border-blue-600 rounded-md py-1 px-2"
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Scroll to top button */}
                {isScrolled && (
                    <button
                        onClick={scrollToTop}
                        className="absolute bottom-4 right-4 p-2 bg-white border border-gray-300 rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 z-20"
                        title="Scroll to top"
                    >
                        <ChevronUp size={16} className="text-gray-600" />
                    </button>
                )}
            </div>
        </>
    );
};

export default CallDetailsPanel;
